/**
 * Route container for the trainer dashboard.
 *
 * Its only job is resolving *which* trainer to show — a trainer sees
 * themselves, an admin picks one. Everything else lives in the
 * dashboard itself, so identity resolution and presentation stay
 * separate concerns.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, UserCog } from 'lucide-react';

import { getMe } from '../../../../services/AuthService';
import { getTrainers } from '../../../../services/TrainerService';
import TrainerService from '../../../../services/TrainerDashboardService';

import '../../../../design/tokens.css';
import { Empty, Loading } from './ui';
import TrainerDashboard from './TrainerDashboard';

const PAGE_SIZE = 10;

export default function TrainerDashboardRoute() {
  const [resolving, setResolving] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [trainerId, setTrainerId] = useState(null);
  const [trainerName, setTrainerName] = useState('');

  // Admin picker
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const initialised = useRef(false);

  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;

    (async () => {
      try {
        const profile = await getMe();
        const user = profile.data;
        const label = String(user?.profiles?.[0]?.label || '').toLowerCase();
        const adminUser = label === 'admin' || Boolean(user?.isSuperAdmin);
        setIsAdmin(adminUser);

        if (adminUser) {
          setResolving(false);
          return;
        }

        const userId = user?.id || user?._id || user?.userId;
        if (!userId) {
          setError('We could not work out which trainer you are. Ask an admin to check your profile.');
          setResolving(false);
          return;
        }

        // A trainer is identified two ways depending on where the id came
        // from — their auth userId, or the trainer record's own id. The
        // dashboard endpoints key on the latter.
        let resolved = userId;
        try {
          const record = await TrainerService.getTrainerByTrainerId(userId);
          if (record?.data) resolved = record.data.id || record.data._id || userId;
        } catch {
          // Fall back to the auth id — the dashboard resolves either form.
        }

        setTrainerId(resolved);
        setTrainerName(`${user.firstName || ''} ${user.lastName || ''}`.trim());
      } catch (err) {
        console.error('Trainer identity resolution failed:', err);
        setError('Failed to load your profile.');
      } finally {
        setResolving(false);
      }
    })();
  }, []);

  const runSearch = useCallback(async (term) => {
    setSearching(true);
    try {
      const resp = await getTrainers(PAGE_SIZE, 1, term);
      setOptions(resp?.data || []);
    } catch (err) {
      console.error('Trainer search failed:', err);
      setOptions([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin || trainerId) return undefined;
    const id = setTimeout(() => runSearch(search), 300);
    return () => clearTimeout(id);
  }, [isAdmin, trainerId, search, runSearch]);

  if (resolving) {
    return (
      <div className="td">
        <Loading>Checking who you are…</Loading>
      </div>
    );
  }

  if (error) {
    return (
      <div className="td">
        <Empty icon={<UserCog size={38} />} title="Dashboard unavailable">{error}</Empty>
      </div>
    );
  }

  /* Admin with nobody chosen yet — pick a trainer first. */
  if (isAdmin && !trainerId) {
    return (
      <div className="td">
        <header className="td-hero">
          <div className="td-hero-grid" aria-hidden="true" />
          <div className="td-hero-inner">
            <div className="td-hero-text">
              <div className="td-eyebrow"><UserCog size={13} /> Admin view</div>
              <h1>Whose dashboard?</h1>
              <p className="td-hero-sub">
                Pick a trainer to see their teaching week, batch health and attendance.
              </p>
            </div>
          </div>
        </header>

        <div className="td-filters">
          <div className="td-field" style={{ gridColumn: '1 / -1' }}>
            <label className="td-label">Search trainers</label>
            <input
              className="td-input"
              type="text"
              autoFocus
              placeholder="Name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <section className="td-card">
          {searching ? (
            <Loading>Searching…</Loading>
          ) : options.length === 0 ? (
            <Empty icon={<Search size={34} />} title="No trainers found">
              {search ? 'Nothing matches that search.' : 'Start typing to find a trainer.'}
            </Empty>
          ) : (
            options.map((t) => (
              <button
                type="button"
                className="td-row td-row-link"
                key={t.id || t._id}
                onClick={() => {
                  setTrainerId(t.id || t._id);
                  setTrainerName(t.trainerName || t.name || 'Trainer');
                }}
              >
                <div className="td-row-main">
                  <div className="td-row-title">{t.trainerName || t.name || 'Unnamed trainer'}</div>
                  <div className="td-row-meta">
                    {t.trainerEmail || t.email}
                    {t.specialization && <> · {t.specialization}</>}
                  </div>
                </div>
              </button>
            ))
          )}
        </section>
      </div>
    );
  }

  return (
    <>
      {isAdmin && (
        <div className="td" style={{ paddingBottom: 0 }}>
          <button
            type="button"
            className="td-btn td-btn-quiet"
            onClick={() => { setTrainerId(null); setTrainerName(''); }}
          >
            ← Pick a different trainer
          </button>
        </div>
      )}
      <TrainerDashboard trainerId={trainerId} trainerName={trainerName} />
    </>
  );
}
